/**
 * @module app.shared
 */

import { ICustomFieldDefinition } from 'app/core/components/custom-fields/interfaces';
import { CustomFieldDefinition } from 'app/core/models/essette-api';

// For 'ess-table' use
export interface ICustomFieldTargetColumn {
    label: string,
    definitionType: string,
    definitionIndex: number,
    isActive: boolean,
    definition?: CustomFieldDefinition | null
}

// For 'ess-table' use
export interface ICustomFieldTargetColumnsContainer {
    text?: ICustomFieldTargetColumn[];
    lookup?: ICustomFieldTargetColumn[];
}

export interface ICustomFieldTargetColumnsSettings {
    definitionCount?: number;
    filterInactive?: boolean;
    sortByLabel?: boolean;
    refreshCache?: boolean;
}

/**
 * Get Custom Column(UDF) Information for `ess-table`
 * @param definitions array of custom field definitions
 * @param definitionCount Specifies and limits the explict size of the "Text" and "Lookup" column arrays, where the array indexes match the respective definition's index value.
 * @returns An array of Custom Field Definition column targets with extended info for `ess-table`
 */
export function getCustomFieldTargetColumns(definitions: CustomFieldDefinition[] = [], definitionCount: number = 5): ICustomFieldTargetColumnsContainer {
    let textFieldColumns: ICustomFieldTargetColumn[] = [];
    let lookupFieldColumns: ICustomFieldTargetColumn[] = [];

    const textFieldDefinitions: CustomFieldDefinition[] = definitions.filter(definition => definition.property?.type === 'text');
    const lookupFieldDefinitions: CustomFieldDefinition[] = definitions.filter(definition => definition.property?.type === 'lookup');

    // Text UDFs
    for(let definitionIndex = 1; definitionIndex <= definitionCount; definitionIndex++) {
        // default values
        textFieldColumns.push({
            label: '(Inactive) User Field Text ' + definitionIndex,
            isActive: false,
            definitionType: 'text',
            definitionIndex: definitionIndex,
            definition: null
        });
    }

    // Replace the default with the real target
    textFieldDefinitions.forEach(definition => {
        const definitionIndex = getCustomFieldTargetIndex(definition);

        if(definitionIndex && definitionIndex > 0) {
            const customFieldDefinition = getCustomFieldDefinitionColumn(definition);
            if(customFieldDefinition) {
                // INFO[MW]: If ess-table ever supports dynamic columns, and we only want to show active UDFs, change this to a .push() and adjust the view to loop over the desired UDFs
                textFieldColumns.splice(definitionIndex - 1, 1, customFieldDefinition);
            }
        }
    });

    // Lookup UDFs
    for(let definitionIndex = 1; definitionIndex <= definitionCount; definitionIndex++) {
        // default values
        lookupFieldColumns.push({
            label: '(Inactive) User Field Lookup ' + definitionIndex,
            isActive: false,
            definitionType: 'lookup',
            definitionIndex: definitionIndex,
            definition: null
        });
    }

    // Replace the default with the real target
    lookupFieldDefinitions.forEach(definition => {
        const definitionIndex = getCustomFieldTargetIndex(definition);

        if(definitionIndex && definitionIndex > 0) {
            const customFieldDefinition = getCustomFieldDefinitionColumn(definition);
            if(customFieldDefinition) {
                // INFO[MW]: If ess-table ever supports dynamic columns, and we only want to show active UDFs, change this to a .push() and adjust the view to loop over the desired UDFs
                lookupFieldColumns.splice(definitionIndex - 1, 1, customFieldDefinition);
            }
        }
    });

    return { text: textFieldColumns, lookup: lookupFieldColumns };
}

/**
 * Get Custom Column(UDF) Information for `ess-table`
 * @param definition custom field definition
 */
function getCustomFieldDefinitionColumn(definition: CustomFieldDefinition): ICustomFieldTargetColumn | null {
    if(!definition.property?.type) { return null; }

    const definitionIndex: number | null = getCustomFieldTargetIndex(definition);
    if(!definitionIndex) { return null; }

    switch(definition.property.type) {
        case 'text':
            return {
                label: definition.$display ?? (definition.isActive ? '' : '(Inactive) ') + 'User Field Text ' + definitionIndex,
                definitionType: definition.property.type,
                definitionIndex: definitionIndex,
                isActive: definition.isActive ?? false,
                definition: definition
            }

        case 'lookup':
            return {
                label: definition.$display ?? (definition.isActive ? '' : '(Inactive) ') + 'User Field Lookup ' + definitionIndex,
                definitionType: definition.property.type,
                definitionIndex: definitionIndex,
                isActive: definition.isActive ?? false,
                definition: definition
            }
        default:
            return null;
    }
}

/**
 * Parse the Definition Index(1-based) from a Custom Field's definition
 * @param definition custom field definition
 */
export function getCustomFieldTargetIndex(definition: ICustomFieldDefinition): number | null {
    // INFO[MW]: Adding a 'definitionIndex' property to ICustomField with support from the API would simplify a lot of the parsing
    if(!definition.property?.type) { return null; }

    switch(definition.property.type) {
        case 'text':
            return (definition.property?.field && definition.property?.field?.includes('value')) ? parseInt(definition.property.field.replace('value', ''), 10) || null : null;
        case 'lookup':
            return (definition.property?.field && definition.property?.field?.includes('choice')) ? parseInt(definition.property.field.replace('choice', ''), 10) || null : null;
        default:
            return null;
    }
}
